/**
 * RESPONSIBILITY: one block of the /adc page.
 *
 * The template is the only field always asked for and it decides every other one, the
 * way an event's band does: pick Gallery and you are asked for pictures and nothing else.
 * Hiding a field never clears it, so changing template and back loses nothing.
 */
import type { ImageValue } from '@framework/core';
import { Row, Text, Paragraph, Img, Bool, Link, Choice, List, RenderOf } from '@framework/core/decorators';
import { ADC_THEMES, ADC_THEME as T } from './themes';
import { FaqRow } from '../events/rows/faq';
import { AdcPictureRow } from './picture';

const WITH_TEXT = [T.Text, T.ImageLeft, T.ImageRight, T.ImageTop, T.TextTop];
const WITH_IMAGE = [T.Image, T.ImageLeft, T.ImageRight, T.ImageTop, T.TextTop];

@Row()
export class AdcBlockRow {
  @Choice({
    label: 'Template', options: ADC_THEMES, default: T.Text,
    description:
      'Choose this first — it decides what you are asked for. '
      + 'Image: one picture. Text: one box of words. '
      + 'Image left, text right · Text left, image right: a picture beside words. '
      + 'Image on top, text below · Text on top, image below: one above the other. '
      + 'Questions and answers: a list people can open. '
      + 'Video: a YouTube video. Gallery: a grid of pictures.',
  })
  theme!: string;

  @Text({ label: 'Heading', maxLength: 300 })
  heading!: string;

  @Bool({ label: 'Show this block', default: true, description: 'Untick to take it off the page without deleting it.' })
  show!: boolean;

  @Paragraph({
    label: 'Text', maxLength: 20000, rows: 6, markdown: true, allow: ['verse'],
    showWhen: { field: 'theme', is: WITH_TEXT },
  })
  body!: string;

  @RenderOf('body')
  bodyHtml!: string;

  @Img({
    label: 'Picture', folder: 'adc', maxSizeMb: 12, size: '1600 × 1000',
    showWhen: { field: 'theme', is: WITH_IMAGE },
  })
  image!: ImageValue;

  @List({
    label: 'Questions', of: () => FaqRow, maxItems: 30,
    showWhen: { field: 'theme', is: [T.Faq] },
  })
  questions!: FaqRow[];

  @Link({
    label: 'YouTube link',
    showWhen: { field: 'theme', is: [T.Video] },
    description: 'Paste the video’s address. It plays on this page.',
  })
  videoHref!: string;

  @List({
    label: 'Pictures', of: () => AdcPictureRow, maxItems: 40,
    showWhen: { field: 'theme', is: [T.Gallery] },
  })
  pictures!: AdcPictureRow[];
}
